import React from "react";
import {
  Box,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import TrackChangesRoundedIcon from "@mui/icons-material/TrackChangesRounded";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import Diversity3OutlinedIcon from "@mui/icons-material/Diversity3Outlined";
import PageIntro from "../components/PageIntro";

const consortiumLogos = [
  { src: "/images/consortium/enco.jpg", alt: "ENCO", href: "https://www.enco-consulting.it/" },
  { src: "/images/consortium/agraria.jpg", alt: "UNINA", href: "https://www.unina.it/it/" },
  { src: "/images/consortium/inrat.png", alt: "INRAT", href: "https://www.inrat.agrinet.tn/" },
  { src: "/images/consortium/iav.jpg", alt: "IAV", href: "https://www.iav.ac.ma/" },
  { src: "/images/consortium/arc.png", alt: "ARC", href: "https://www.arc.sci.eg/" },
  { src: "/images/consortium/ceeba.png", alt: "CEEBA", href: "https://www.ceeba.org/" },
  { src: "/images/consortium/narc.png", alt: "NARC", href: "https://www.ncare.gov.jo/" },
  { src: "/images/consortium/ffi.png", alt: "Future Food Institute", href: "https://www.futurefoodinstitute.org/" },
  { src: "/images/consortium/itene.png", alt: "ITENE", href: "https://www.itene.com/" },
  { src: "/images/consortium/certh.png", alt: "CERTH / ITI-CERTH", href: "https://www.certh.gr/" },
  { src: "/images/consortium/cma.jpg", alt: "CMA", href: "https://www.rose-blanche.com/en/our-sectors/agricultural-and-poultry/cma/" },
  { src: "/images/consortium/healthTech.png", alt: "HealthTech", href: "https://www.healthtech-eg.com/" },
  { src: "/images/consortium/ingc.png", alt: "INGC", href: "https://www.ingc.com.tn/" },
  { src: "/images/consortium/caiac.png", alt: "CAIAC", href: "https://www.caiac.ma/" },
  { src: "/images/consortium/josilos.png", alt: "JOSILOS", href: "https://www.josilos.com/" },
  { src: "/images/consortium/cnta.png", alt: "CNTA", href: "https://www.cnta.es/" },
];

const cardSx = {
  p: { xs: 3, md: 4 },
  bgcolor: "#fff",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
};

function SectionHeading({ icon: Icon, children }) {
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Box
        sx={{
          width: 48,
          height: 48,
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          borderRadius: 1.25,
          color: "#f5a900",
          bgcolor: "#fff7df",
        }}
      >
        <Icon />
      </Box>
      <Typography variant="h4" sx={{ fontSize: { xs: 21, md: 24 } }}>
        {children}
      </Typography>
    </Stack>
  );
}

function Feature({ icon: Icon, title, children, color, background }) {
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Box
        sx={{
          width: 48,
          height: 48,
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          borderRadius: 1.25,
          color,
          bgcolor: background,
        }}
      >
        <Icon />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 0.75 }}>
          {title}
        </Typography>
        <Typography
          color="text.secondary"
          sx={{ fontSize: 13, lineHeight: 1.65 }}
        >
          {children}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function About() {
  return (
    <Box sx={{ bgcolor: "#fcfcfc", minHeight: "100%" }}>
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 5 } }}>
        <PageIntro title="About" sx={{ mb: 4 }}>
          Sustainable agricultural research and innovation for the Mediterranean
          region
        </PageIntro>

        <Paper component="section" elevation={0} sx={{ ...cardSx, mb: 4 }}>
          <SectionHeading icon={TrackChangesRoundedIcon}>
            What we do
          </SectionHeading>

          <Typography
            sx={{
              mt: 3.5,
              fontSize: 14,
              lineHeight: 1.8,
              textAlign: { xs: "justify", md: "justify" },
            }}
          >
            The SEEDS platform aims to revitalize ancient grains to enhance cereal supply chain resilience in the Mediterranean region, particularly in Tunisia, Egypt, Morocco, and Jordan.
By reintroducing ancient grains, SEEDS promotes agricultural biodiversity and reduces reliance on a limited set of grain crops. Through testing adaptability to current conditions and pests, SEEDS aims to provide evidence-based recommendations for increasing supply chain resilience and digitalization. Utilizing multi-criteria decision analysis and living labs, the project engages local communities and stakeholders to develop innovative solutions.
A user-friendly digital platform for crop monitoring and a smart supply chain facilitate efficient interconnection among stakeholders, fostering healthier and sustainable food options. SEEDS also seeks to replicate its model in other Mediterranean countries to enhance knowledge sharing and digitalization.
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: { xs: 3, md: 5 },
              mt: 4,
            }}
          >
            <Feature
              icon={Diversity3OutlinedIcon}
              title="Research & Innovation"
              color="#1265ed"
              background="#e1edff"
            >
              Advancing agricultural science through collaborative research and
              innovative methodologies
            </Feature>
            <Feature
              icon={TrackChangesRoundedIcon}
              title="Data-Driven Solutions"
              color="#00a95c"
              background="#dcf8e9"
            >
              Leveraging technology and data analytics to support informed
              decision-making
            </Feature>
          </Box>
        </Paper>

        <Paper component="section" elevation={0} sx={cardSx}>
          <SectionHeading icon={Groups2OutlinedIcon}>Consortium</SectionHeading>

          <Typography
            // color="text.secondary"
            sx={{
              mt: 3.5,
              fontSize: 14,
              lineHeight: 1.8,
              textAlign: { xs: "justify", md: "justify" },
            }}
          >
            SEEDS consortium is composed of experienced partners, active in 7 countries of the Mediterranean area (IT, ES, EL, EG, JO, MA, TN); through a close cooperation we collaborate to transform an idea into a real project aimed to revitalize the culture of ancient grains. Their resilience to adversities inspired us to create an action plan focused on enhancing the supply chain of cereal to face the ever-growing consumers’ demand, especially in MENA countries. Our consortium consists of 16 partners with deep knowledge in the agri-food sector: 5 innovative SMEs (ENCO, HEALTHTECH, JOSILOS, CAIAC, CMA); 9 centers for research (UNINA, ARC, CNTA, ITENE, ITI/CERTH, NARC, IAV, INRAT, INGC); 1 cultural association (FFI) and 1 organization (CEEBA). Using an interdisciplinary approach, we will provide innovative solutions for sustainable development in the MENA region and raise awareness in consumers, retailers, and policymakers about the health benefits they can obtain by introducing ancient grains in their diets.
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(3, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
              },
              gap: 1.5,
              mt: 3,
            }}
          >
            {consortiumLogos.map((logo) => (
              <Box
                component={logo.href ? "a" : "div"}
                key={logo.src}
                href={logo.href}
                target={logo.href ? "_blank" : undefined}
                rel={logo.href ? "noopener noreferrer" : undefined}
                aria-label={logo.href ? `Open ${logo.alt} website` : undefined}
                sx={{
                  aspectRatio: "1 / 1",
                  display: "grid",
                  placeItems: "center",
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: "#fff",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.25,
                  textDecoration: "none",
                  overflow: "hidden",
                  transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
                  ...(logo.href && {
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: "#f5a900",
                      boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
                      transform: "translateY(-2px)",
                    },
                  }),
                }}
              >
                <Box
                  component="img"
                  src={logo.src}
                  alt={logo.alt}
                  loading="lazy"
                  sx={{
                    display: "block",
                    maxWidth: logo.alt === "CERTH / ITI-CERTH" ? "120%" : "100%",
                    maxHeight: logo.alt === "CERTH / ITI-CERTH" ? "120%" : "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            ))}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
